import { Link } from 'react-router-dom';

import { DocPage } from '@/components/doc-page';
import { DynamicText } from '@/components/dynamic-text';

export default function PrivacyPage() {
  return (
    <DocPage
      kicker={<DynamicText path="privacy.kicker">Privacy policy</DynamicText>}
      title={<DynamicText path="privacy.title">Privacy Policy</DynamicText>}
      lead={
        <DynamicText path="privacy.lead">
          You can use Bramble for simple tracking without an account. This page explains what we hold when you create one, who can reach it, and what we do not do with it.
        </DynamicText>
      }
      updated={<DynamicText path="privacy.updated">14 September 2026</DynamicText>}
    >
      <h2>
        <DynamicText path="privacy.summary.heading">Summary</DynamicText>
      </h2>
      <ul>
        <li>
          <DynamicText path="privacy.summary.item1">
            No account required for simple tracking. You can use Bramble to keep a log without signing up.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.summary.item2">
            Syncing with a co-parent requires accounts. You will each need to create one using an email address and password. That is how your devices find the same log. Everything you logged before creating an account is uploaded when you sign up.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.summary.item3">
            Sharing is a paid feature. Bramble Sharing is $4.99/month in the United States and covers up to four registered caregivers. Apple handles the payment; we receive transaction and subscription status, not your card or bank details.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.summary.item4">
            We store your child’s log. Names, birth dates, feeds, diapers, sleep, potty trips, growth, health notes, medicines, milestones, and anything you type into a note are stored on our server so they can sync.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.summary.item5">
            We can technically read it. The data is encrypted in transit and encrypted at rest on disk, but it is not end-to-end encrypted: the server holds the keys it needs to serve your timeline. We do not read it, profile it, sell it, or share it. But we will not tell you we cannot, because that would not be true.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.summary.item6">
            No tracking, no ads, no analytics SDKs. Unchanged, and not changing.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.summary.item7">
            It still works offline. The app keeps a full copy on your phone and logs without a connection, syncing when one returns.
          </DynamicText>
        </li>
      </ul>

      <h2>
        <DynamicText path="privacy.what_we_store.heading">What we store</DynamicText>
      </h2>
      <h3>
        <DynamicText path="privacy.account.heading">Your account</DynamicText>
      </h3>
      <ul>
        <li>
          <DynamicText path="privacy.account.item1">
            Email address. Used to identify your account and to reach you about it.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.account.item2">
            Password. Stored only as a slow one-way hash (PBKDF2-HMAC-SHA256 with a per-account random salt). We cannot recover it, and neither can anyone who obtains the database.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.account.item3">
            Session tokens. Stored hashed, expire, and are replaced each time they are used.
          </DynamicText>
        </li>
      </ul>

      <h3>
        <DynamicText path="privacy.child_log.heading">Your child’s log</DynamicText>
      </h3>
      <p>
        <DynamicText path="privacy.child_log.p1">
          Each child record holds a name, a birth date, and your display settings. Each logged event holds its type, its timestamps, and its details — a bottle volume, a nap length, a weight, the text of a note. All of it is stored on our server, and all of it is readable by the server in order to show you a timeline.
        </DynamicText>
      </p>

      <h3>
        <DynamicText path="privacy.subscription.heading">Subscription information</DynamicText>
      </h3>
      <p>
        <DynamicText path="privacy.subscription.p1">
          If you buy or are covered by Bramble Sharing, we store the App Store product, a reported transaction identifier, purchase and expiry dates, courtesy-period or revocation status, App Store environment, and the Bramble account and caregiver seats covered by the subscription. We use this to prevent one subscription from covering more than four registered caregivers and decide whether Sharing is active. Apple processes the purchase itself; your payment-card or bank details never reach Bramble.
        </DynamicText>
      </p>

      <h3>
        <DynamicText path="privacy.you_section.heading">The You section is yours alone</DynamicText>
      </h3>
      <p>
        <DynamicText path="privacy.you_section.p1">
          The You section — your own daily mood check-ins and journal notes — is stored on our server and synced across the devices you are signed in on, so it survives losing your phone. It is never visible to your co-parent, to a babysitter, or to anyone else you share a child’s log with. How a caregiver is doing is not a shared fact about the child, and putting it on a co-parent’s phone would be a privacy failure rather than a feature.
        </DynamicText>
      </p>
      <p>
        <DynamicText path="privacy.you_section.p2">
          That separation is built into the database rather than enforced by a rule we have to remember: these rows are keyed to your user account and have no family column to join through, so there is no query that could return them to somebody else. They are stored in a form we can read, the same as everything else described above.
        </DynamicText>
      </p>
      <h3>
        <DynamicText path="privacy.milestones.heading">Milestone media</DynamicText>
      </h3>
      <p>
        <DynamicText path="privacy.milestones.p1">
          Photos and videos attached to a milestone stay on the device they were added on. What syncs is the milestone itself, not the media.
        </DynamicText>
      </p>

      <h3>
        <DynamicText path="privacy.notifications.heading">Notifications</DynamicText>
      </h3>
      <p>
        <DynamicText path="privacy.notifications.p1">
          If you enable them, we store an Apple push notification token so your co-parent’s change can wake your phone. Notifications carry no content — they wake the app, which then fetches the change itself.
        </DynamicText>
      </p>

      <h3>
        <DynamicText path="privacy.do_not_collect.heading">What we do not collect</DynamicText>
      </h3>
      <p>
        <DynamicText path="privacy.do_not_collect.p1">
          No advertising identifiers, no device fingerprints, no location, no contacts, no analytics events, no third-party SDKs of any kind. We do not track you across other apps or sites, and there is nothing in Bramble that could.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="privacy.reach.heading">Who can reach your data</DynamicText>
      </h2>
      <ul>
        <li>
          <DynamicText path="privacy.reach.item1">
            You, and the caregivers you invite. A co-parent sees every child in your family. A babysitter you invite sees exactly one child, and only that child.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.reach.item2">
            Us, technically. Operating a database means being able to query it. We access it to keep the service running and to investigate a problem you report — not to read your records, and never to build a profile of you or your child.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.reach.item3">
            Our infrastructure providers. Named below. They hold the data on their hardware and are bound by their own agreements.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="privacy.reach.item4">
            Nobody else. We do not sell your data, share it with advertisers, or hand it to third parties for their own purposes. If we are ever compelled by law to disclose something, we will tell you unless we are legally barred from doing so.
          </DynamicText>
        </li>
      </ul>

      <h2>
        <DynamicText path="privacy.where_lives.heading">Where it lives</DynamicText>
      </h2>
      <ul>
        <li>
          <strong>Database.</strong> PostgreSQL hosted by{' '}
          <a href="https://neon.com/privacy-policy" rel="noreferrer noopener" target="_blank">
            Neon
          </a>{' '}
          in the United States. Encrypted at rest.
        </li>
        <li>
          <strong>API and website.</strong>{' '}
          <a href="https://www.cloudflare.com/privacypolicy/" rel="noreferrer noopener" target="_blank">
            Cloudflare
          </a>
          , which processes standard request metadata such as IP addresses.
        </li>
        <li>
          <DynamicText path="privacy.where_lives.notifications">
            Notifications. Apple Push Notification service, which receives a device token and an empty wake signal.
          </DynamicText>
        </li>
        <li>
          <strong>Purchases.</strong>{' '}
          <a href="https://www.apple.com/legal/privacy/" rel="noreferrer noopener" target="_blank">
            Apple
          </a>{' '}
          processes Bramble Sharing purchases and sends signed transaction and subscription-status
          information to Bramble so we can provide access.
        </li>
        <li>
          <strong>Support email.</strong>{' '}
          <a href="https://resend.com/legal/privacy-policy" rel="noreferrer noopener" target="_blank">
            Resend
          </a>
          , which delivers messages sent from the support page.
        </li>
      </ul>
      <p>
        <DynamicText path="privacy.where_lives.tls">
          All traffic between your phone and our servers is encrypted with TLS.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="privacy.rights.heading">Your rights</DynamicText>
      </h2>
      <p>
        You can request a copy of everything we hold for your account, or ask us to delete it, by
        writing to us from the <Link to="/support">support page</Link> using the email address on
        the account. Deletion removes your account, your children, and every event logged against
        them. It is permanent, and we cannot undo it.
      </p>
      <p>
        <DynamicText path="privacy.rights.p2">
          Deleting a shared family’s data affects your co-parent too, so we will confirm with you before acting on a request that would remove records another caregiver still relies on.
        </DynamicText>
      </p>
      <p>
        <DynamicText path="privacy.rights.p3">
          Backups are retained for a short period after deletion as a matter of ordinary operation and age out on their own.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="privacy.children.heading">Children’s privacy</DynamicText>
      </h2>
      <p>
        <DynamicText path="privacy.children.p1">
          Bramble is used by adults to record information about the children in their care. We do not knowingly collect information directly from a child, and there is nothing in the app directed at one. Records about a child exist because a caregiver entered them, and that caregiver controls them.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="privacy.website.heading">Website</DynamicText>
      </h2>
      <p>
        <DynamicText path="privacy.website.p1">
          This site uses no tracking cookies and no analytics scripts. Google Fonts are served directly. Support form submissions are retained only long enough to answer you. Please do not include medical details in a support request.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="privacy.changes.heading">Changes</DynamicText>
      </h2>
      <p>
        <DynamicText path="privacy.changes.p1">
          If this policy changes, the updated date at the top of this page changes with it, and any change that affects what we collect or who can reach it will also appear in the app’s release notes.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="privacy.contact.heading">Contact</DynamicText>
      </h2>
      <p>
        Questions about this policy can be sent from the <Link to="/support">support page</Link>.
      </p>
    </DocPage>
  );
}
